import React, { Dispatch, SetStateAction, useState } from 'react'
import {
  BackButtonContainer,
  BreadCrumbsContainer,
  Container,
  Message,
  RelatedContentContainer,
  RelatedProcedureSkeleton,
  ReturnArrowButton
} from './styles'
import { DiagnosisDetailsContent } from './content'
import { HealthProcedureDetails } from '../healthProcedureDetails'
import { ModalHeaderSkeleton } from '../ModalComponents/ModalHeader/Skeleton'
import { ModalButtonComeBack } from '../ModalComponents/ModalButtonComeBack'
import { AnatomopathologicalTestDetailsContent } from '../AnatomopathologicalTestDetails/content'
import { ImagingTestDetailsContent } from '../ImagingTestDetails/content'
import { useFetchOncologyDiagnoses } from '~/hooks'
import { EHealthProceduresTypes, IAnatomopathologicalTest, IHealthProcedures, IImagingTest } from '~/interfaces'
import { BreadCrumbs, BreadCrumbsProps, DataNotFound } from '~/components/shared'
import { IconComponent } from '~/assets/icons/component'

export type RelatedTestType = EHealthProceduresTypes.ANATOMOPATHOLOGICAL_TEST | EHealthProceduresTypes.IMAGING_TEST

export interface DiagnosisDetailsProps {
  healthProcedure: IHealthProcedures
  showModal: boolean
  setShowModal: Dispatch<SetStateAction<boolean>>
}

const Loader = () => (
  <>
    <ModalHeaderSkeleton />
    <RelatedProcedureSkeleton />
    <RelatedProcedureSkeleton />
  </>
)

function isAnatomopathologicalTest(test: IImagingTest | IAnatomopathologicalTest): test is IAnatomopathologicalTest {
  return test.type === EHealthProceduresTypes.ANATOMOPATHOLOGICAL_TEST
}

function isImagingTest(test: IImagingTest | IAnatomopathologicalTest): test is IImagingTest {
  return (test as IImagingTest)?.accessionNumber !== undefined
}

export const DiagnosisDetails = ({ healthProcedure, showModal, setShowModal }: DiagnosisDetailsProps): JSX.Element => {
  const [breadCrumbsLevels, setBreadCrumbsLevels] = useState<BreadCrumbsProps['breadCrumbsLevels']>([])
  const [selectedTestDetail, setSelectedTestDetail] = useState<IImagingTest | IAnatomopathologicalTest>()

  const {
    data: oncologyDiagnosis,
    isLoading: isOncologyDiagnosisLoading,
    error: oncologyDiagnosisError
  } = useFetchOncologyDiagnoses({
    pathParams: { diagnosisId: healthProcedure.id }
  })

  const returnToDiagnosisDetailsModal = () => setSelectedTestDetail(undefined)

  const navigateToRelatedMedicalEventDetails = (selectedTest?: IImagingTest | IAnatomopathologicalTest) => {
    if (selectedTest) {
      setSelectedTestDetail(selectedTest)
      setBreadCrumbsLevels([
        { text: String(oncologyDiagnosis?.description), handleClick: returnToDiagnosisDetailsModal },
        { text: selectedTest.description, handleClick: () => {} }
      ])
    }
  }

  const handleOpenAnatomoPathologicalTest = (id: string) => {
    const selectedTest = oncologyDiagnosis?.anatomopathologicalTests.find(test => test.id === id)
    navigateToRelatedMedicalEventDetails(selectedTest)
  }

  const handleOpenImagingTest = (id: string) => {
    const selectedTest = oncologyDiagnosis?.imagingTests.find(test => test.id === id)
    navigateToRelatedMedicalEventDetails(selectedTest)
  }

  const onOpenRelatedTest = (id: string, type: RelatedTestType) => {
    if (type === EHealthProceduresTypes.ANATOMOPATHOLOGICAL_TEST) handleOpenAnatomoPathologicalTest(id)
    if (type === EHealthProceduresTypes.IMAGING_TEST) handleOpenImagingTest(id)
  }

  const renderContent = () => {
    if (oncologyDiagnosisError) {
      return (
        <Message>
          <DataNotFound text={oncologyDiagnosisError.message} />
        </Message>
      )
    }

    if (selectedTestDetail) {
      return (
        <>
          <ReturnArrowButton onClick={returnToDiagnosisDetailsModal}>
            <IconComponent name="arrowLeft" />
          </ReturnArrowButton>

          <BreadCrumbsContainer>
            <BreadCrumbs breadCrumbsLevels={breadCrumbsLevels} />
          </BreadCrumbsContainer>
          <RelatedContentContainer>
            {isAnatomopathologicalTest(selectedTestDetail) && (
              <AnatomopathologicalTestDetailsContent anatomopathologicalTestData={selectedTestDetail} />
            )}

            {isImagingTest(selectedTestDetail) && <ImagingTestDetailsContent imagingTestDetails={selectedTestDetail} />}
          </RelatedContentContainer>
          <BackButtonContainer>
            <ModalButtonComeBack onClick={returnToDiagnosisDetailsModal} />
          </BackButtonContainer>
        </>
      )
    }

    if (oncologyDiagnosis) {
      return <DiagnosisDetailsContent diagnosisData={oncologyDiagnosis} onOpenRelatedTest={onOpenRelatedTest} />
    }
  }

  return (
    <HealthProcedureDetails showModal={showModal} setShowModal={setShowModal} viewType="SideModal">
      <Container>{isOncologyDiagnosisLoading ? <Loader /> : renderContent()}</Container>
    </HealthProcedureDetails>
  )
}
